// Epic at THI: what to document, by Epic's own tabs. Kept at the level of
// tasks rather than clicks, since the hospital's Epic build changes on
// 2026-10-31 and specifics may move while the sequence stays the same.
//
//   step { title, details?: string[], who?: 'attending', optional?: true }

export const EPIC_TRANSITION_NOTE =
  'Epic is changing on October 31, 2026. Specific buttons and tabs may move; the sequence of tasks should still hold.';

export const caseDocumentation = {
  pre: [
    { title: 'Review the patient', details: ['Use At a Glance for current status'] },
    { title: 'Allergies' },
    {
      title: 'Pre Evaluation note',
      details: [
        'Use the NoteWriter template',
        'ROS / Med Hx tab: check pertinent positives and negatives in every section',
        'Physical Exam tab: complete after examining the patient',
        'Anesthesia Plan tab: select only the primary anesthetic type (not TIVA)',
      ],
      footnote: { text: 'Only attendings can click Pre-Evaluation Note Complete at the bottom of the note', who: 'attending' },
    },
    { title: 'Review and sign', details: ['Mark PreEval Note Reviewed', 'Ready for Procedure'] },
  ],
  intra: [
    { title: 'Macro', details: ['Load a macro for the case type if you like'], optional: true },
    { title: 'Staff' },
    { title: 'Check', details: ['Document the case checklist'] },
    { title: 'Attest', who: 'attending' },
    { title: 'Airways' },
    { title: 'Lines and drains' },
    { title: 'Position' },
    { title: 'Px Notes' },
    { title: 'Meds' },
  ],
  post: [
    { title: 'Handoff', details: ['Mark the time you gave handoff to the receiving team'] },
    { title: 'Vitals', details: ['Including temperature'] },
    { title: 'Notable Events' },
    { title: 'Post Evaluation note' },
    { title: 'Anesthesia Stop' },
    { title: 'Sign Record', who: 'attending' },
  ],
};

// Medical direction: the seven conditions of 42 CFR 415.110(a)(1), quoted.
export const MEDICAL_DIRECTION = {
  intro:
    'All cases at THI are billed as medical direction. The attestation defaults in our Epic build do not cover the required elements, so attendings document them by hand with the SmartPhrases below.',
  elements: [
    'Performs a pre-anesthetic examination and evaluation',
    'Prescribes the anesthesia plan',
    'Personally participates in the most demanding aspects of the anesthesia plan including, if applicable, induction and emergence',
    'Ensures that any procedures in the anesthesia plan that he or she does not perform are performed by a qualified individual',
    'Monitors the course of anesthesia administration at frequent intervals',
    'Remains physically present and available for immediate diagnosis and treatment of emergencies',
    'Provides indicated post-anesthesia care',
  ],
  // Kept for the Department Resources billing page; not shown on the Epic tab
  limits: [
    'Direct no more than four anesthesia services at once, and perform no other services while directing',
    'Document in the record that each element was met, specifically the pre-anesthetic exam and evaluation, presence for the most demanding aspects including induction and emergence, and the post-anesthesia care',
  ],
  // Activities CMS allows while medically directing (Claims Processing Manual, ch. 12, §50)
  permitted: [
    'Addressing an emergency of short duration in the immediate area',
    'Administering an epidural or caudal for labor analgesia',
    'Periodic monitoring of an obstetrical patient',
    'Receiving a patient into the OR for the next case',
    'Checking on or discharging patients in the PACU',
    'Coordinating scheduling matters',
  ],
  references: [
    { label: '42 CFR § 415.110 (Cornell LII)', url: 'https://www.law.cornell.edu/cfr/text/42/415.110' },
    { label: 'CMS Claims Processing Manual, ch. 12 §50', url: 'https://www.cms.gov/regulations-and-guidance/guidance/manuals/downloads/clm104c12.pdf' },
  ],
  smartPhrases: [
    {
      phrase: '.preopattestation',
      use: 'Pre Evaluation note',
      phase: 'PreProcedure',
      note: 'Only needed when the preop was completed by a trainee or CAA',
    },
    {
      phrase: '.cvalineattestation',
      use: 'Anesthesia monitoring lines',
      phase: 'IntraProcedure · place as a QNote',
      note: 'CMS requires specific documentation for the arterial line; this phrase covers all lines, which is more than required',
    },
    { phrase: '.teeattestation', use: 'TEE procedure note', phase: 'IntraProcedure' },
    { phrase: null, use: 'Anesthesia procedures', phase: 'IntraProcedure', pending: true },
    { phrase: null, use: 'Post Evaluation note', phase: 'PostProcedure', pending: true },
  ],
};

/** Orders & Templates tab: what to search for in Epic, by name */
export const ORDERS = {
  common: [
    { name: 'RRL panel', note: 'Rapid response lab' },
    { name: 'Lab9999, Misc test', note: 'How ROTEM is ordered' },
    { name: 'Coagulogram', note: 'aPTT, PT, INR, fibrinogen' },
  ],
  ordersets: [
    { name: 'Adult Anesthesia PACU' },
    { name: 'CV Anesthesia Peripheral Nerve Block Panel', note: 'Subject to change with the Eitan Sapphire go-live' },
    { name: 'Peripheral Nerve Block Inpatient' },
    { name: 'Adult Hemodialysis', note: 'Heparin packing doses for dialysis catheters' },
    { name: 'CV Anesthesia Epidural' },
    { name: 'Bronchoscopy Specimens' },
  ],
  templatesPending: 'Note templates: coming.',
};
